import { Backend, Blend, DynamicMesh, FLOATS_PER_VERTEX, Frame, Mesh, PACKED_STRIDE, quadIndices } from "./backend";

// The fallback: plain WebGL 1, for devices without WebGPU. It draws exactly
// what renderer.ts hands it — the shading here must match backend-gpu.ts's.

const VS = `
attribute vec3 aPos; attribute vec3 aNormal; attribute vec4 aColor; attribute vec2 aUv;
uniform mat4 uViewProj; uniform mat4 uModel;
varying vec3 vNormal; varying vec4 vColor; varying vec2 vUv; varying vec3 vWorld;
void main() {
  vec4 w = uModel * vec4(aPos, 1.0);
  vWorld = w.xyz;
  vNormal = (uModel * vec4(aNormal, 0.0)).xyz;
  vColor = aColor;
  vUv = aUv;
  gl_Position = uViewProj * w;
}`;

// Modes: 0 lit colour, 1 road (checker + start line), 2 boost pad, 3 sprite,
// 4 soft blob (shadow, smoke), 5 exhaust flame, 6 ember glow, 7 light beam,
// 8 unlit (the sky, the hills round it), 9 lit with a grain (the ground, the road),
// 10 lit, faded by dithering to the tint's alpha (a kart in front of the camera).
const FS = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform float uMode; uniform vec4 uTint; uniform float uTime; uniform vec3 uCam; uniform vec3 uFog;
uniform sampler2D uTex; uniform vec3 uBody; uniform vec3 uAccent; uniform vec3 uSkin; uniform vec3 uHair;
uniform float uShine; uniform float uLook; uniform vec3 uSun; uniform vec3 uSky; uniform vec3 uGround;
varying vec3 vNormal; varying vec4 vColor; varying vec2 vUv; varying vec3 vWorld;
// A hash without sin (cheaper, and steady at mediump): Dave Hoskins's hash12.
// An ordered 4×4 dither, 0…1, by pixel.
float bayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
float bayer4(vec2 a) { return bayer2(0.5 * a) * 0.25 + bayer2(a); }
float hash(vec2 p) {
  vec3 q = fract(vec3(p.xyx) * 0.1031);
  q += dot(q, q.yzx + 33.33);
  return fract((q.x + q.y) * q.z);
}
// Value noise, 0…1.
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
void main() {
  if (uMode > 2.5 && uMode < 3.5) {
    vec4 t = texture2D(uTex, vUv);
    if (t.a < 0.45) discard;
    // Over its alpha: a mipmap's edge texels average in the transparent black round a sprite.
    gl_FragColor = vec4(t.rgb / t.a * uTint.rgb, 1.0);
    return;
  }
  if (uMode > 3.5 && uMode < 4.5) {
    float d = length(vUv - 0.5) * 2.0;
    gl_FragColor = vec4(vColor.rgb, (1.0 - smoothstep(0.4, 1.0, d)) * uTint.a);
    return;
  }
  if (uMode > 4.5 && uMode < 5.5) {
    // Along the flame (v: 0 at the pipe, 1 at the tip): white-hot, then
    // orange, then a red that fades out — licking, and softest at its edges.
    float t = vUv.y;
    float lick = 0.75 + 0.25 * sin(uTime * 55.0 + vUv.x * 25.0 - t * 14.0);
    vec3 col = mix(vec3(1.0, 0.96, 0.78), vec3(1.0, 0.5, 0.08), smoothstep(0.05, 0.5, t));
    col = mix(col, vec3(0.85, 0.1, 0.02), smoothstep(0.5, 1.0, t));
    float edge = pow(abs(dot(normalize(vNormal), normalize(uCam - vWorld))), 0.7);
    float a = (1.0 - smoothstep(0.35, 1.0, t)) * lick * edge * uTint.a;
    gl_FragColor = vec4(col * uTint.rgb * a * 1.2, 1.0);
    return;
  }
  if (uMode > 7.5 && uMode < 8.5) {
    gl_FragColor = vec4(vColor.rgb, 1.0);
    return;
  }
  if (uMode > 6.5 && uMode < 7.5) {
    // A laser or a light's beam (u across it, v along it): brightest down
    // the middle, fading towards the far end.
    float across = 1.0 - abs(vUv.x - 0.5) * 2.0;
    gl_FragColor = vec4(uTint.rgb * across * across * (1.0 - 0.75 * vUv.y) * uTint.a, 1.0);
    return;
  }
  if (uMode > 5.5 && uMode < 6.5) {
    float d = length(vUv - 0.5) * 2.0;
    gl_FragColor = vec4(vColor.rgb * (1.0 - smoothstep(0.0, 1.0, d)), 1.0);
    return;
  }
  // Paint slots: a model's alpha says which colour goes there, over the
  // shading baked into its grey — 0 body, 64 skin, 128 accent, 192 hair,
  // 255 none.
  vec3 base = vColor.rgb;
  if (vColor.a < 0.125) base *= uBody;
  else if (vColor.a < 0.375) base *= uSkin;
  else if (vColor.a < 0.625) base *= uAccent;
  else if (vColor.a < 0.875) base *= uHair;
  // Faded by leaving out pixels, not by blending: what's behind the face — and
  // the face behind the head — stays hidden, as it does with the depth test.
  if (uMode > 9.5 && uTint.a <= bayer4(gl_FragCoord.xy)) discard;
  float lit = 1.0;
  if (uMode > 0.5 && uMode < 1.5) {
    float c = mod(floor(vUv.x * 3.0) + floor(vUv.y / 4.67), 2.0);
    base = vColor.rgb * (1.0 + 0.2 * c);
    if (vUv.y < 2.4) base = vec3(0.06 + 0.9 * mod(floor(vUv.x * 14.0) + floor(vUv.y * 2.5), 2.0));
  } else if (uMode > 8.5 && uMode < 9.5) {
    // Grain in world space, at two scales so neither shows a repeat.
    base = base * (0.9 + 0.13 * noise(vWorld.xz * 0.35) + 0.08 * noise(vWorld.xz * 2.7));
  } else if (uMode > 1.5 && uMode < 2.5) {
    float ch = fract(vUv.y * 2.5 + abs(vUv.x - 0.5) * 1.4 - uTime * 2.2);
    base = mix(vec3(1.0, 0.5, 0.05), vec3(1.0, 0.88, 0.25), step(0.5, ch));
    lit = 0.0;
  }
  vec3 n = normalize(vNormal);
  vec3 v = normalize(uCam - vWorld);
  if (uLook > 0.5) {
    // The revamp's light: a hemisphere of blue sky above and warm ground
    // below, a sun from over the camera's shoulder, a glossy highlight and a
    // fresnel of sky on the models; a touch more saturated, fogged into the horizon.
    vec3 hemi = mix(uGround, uSky, 0.5 + 0.5 * n.y);
    vec3 light = mix(vec3(1.0), hemi + vec3(1.0, 0.93, 0.8) * max(dot(n, uSun), 0.0) * 0.62, lit);
    float spec = pow(max(dot(n, normalize(uSun + v)), 0.0), 60.0) * 0.55;
    float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0) * 0.8;
    vec3 col = (base * light + (vec3(spec) + uSky * fres) * uShine) * uTint.rgb;
    // Graded: 15 % more saturated, highlights a touch warmer and shadows cooler.
    float luma = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(luma), col, 1.15) + (luma - 0.5) * vec3(0.06, 0.0, -0.06);
    float fog = smoothstep(110.0, 300.0, distance(vWorld, uCam));
    gl_FragColor = vec4(mix(col, uFog, fog), uTint.a);
    return;
  }
  vec3 l = normalize(vec3(0.35, 1.0, 0.25));
  float diffuse = max(dot(n, l), 0.0);
  float light = mix(1.0, 0.45 + 0.2 * (0.5 + 0.5 * n.y) + 0.45 * diffuse, lit);
  // Toy plastic: a tight highlight and a soft rim, on the models only.
  float spec = pow(max(dot(n, normalize(l + v)), 0.0), 40.0) * 0.45;
  float rim = pow(1.0 - max(dot(n, v), 0.0), 3.0) * 0.22;
  vec3 col = (base * light + (spec + rim) * uShine) * uTint.rgb;
  float fog = smoothstep(70.0, 170.0, distance(vWorld, uCam));
  gl_FragColor = vec4(mix(col, uFog, fog), uTint.a);
}`;

interface GlMesh extends Mesh {
  vbo: WebGLBuffer;
  ibo: WebGLBuffer;
}

const PASSES: Blend[] = ["opaque", "alpha", "add"];

export function createGlBackend(canvas: HTMLCanvasElement): Backend | null {
  const gl = canvas.getContext("webgl", { antialias: true, alpha: false }) as WebGLRenderingContext | null;
  if (!gl) return null;

  const compile = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? "shader");
    return s;
  };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
  gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(prog);
  gl.useProgram(prog);
  const attr = (name: string) => gl.getAttribLocation(prog, name);
  const locs = { pos: attr("aPos"), normal: attr("aNormal"), color: attr("aColor"), uv: attr("aUv") };
  const u = (name: string) => gl.getUniformLocation(prog, name);
  const uni = {
    viewProj: u("uViewProj"), model: u("uModel"), mode: u("uMode"), tint: u("uTint"),
    time: u("uTime"), cam: u("uCam"), fog: u("uFog"), tex: u("uTex"),
    body: u("uBody"), accent: u("uAccent"), skin: u("uSkin"), hair: u("uHair"), shine: u("uShine"),
    look: u("uLook"), sun: u("uSun"), sky: u("uSky"), ground: u("uGround"),
  };
  Object.values(locs).forEach((l) => gl.enableVertexAttribArray(l));
  gl.enable(gl.DEPTH_TEST);

  const texture = gl.createTexture();
  const WHITE3 = new Float32Array([1, 1, 1]);
  const F = 4;

  const buffers = (vertices: BufferSource, indices: Uint16Array, usage: number) => {
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, usage);
    const ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    return { vbo, ibo };
  };

  const setBlend = (blend: Blend) => {
    if (blend === "opaque") {
      gl.disable(gl.BLEND);
      gl.depthMask(true);
      return;
    }
    gl.enable(gl.BLEND);
    gl.depthMask(false);
    if (blend === "alpha") gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    else gl.blendFunc(gl.ONE, gl.ONE);
  };

  return {
    kind: "webgl",
    depthZeroToOne: false,
    lost: false,
    mesh(vertices, indices, packed): GlMesh {
      return { ...buffers(vertices, indices, gl.STATIC_DRAW), count: indices.length, packed };
    },
    dynamicMesh(maxQuads): DynamicMesh & GlMesh {
      const data = new Float32Array(maxQuads * 4 * FLOATS_PER_VERTEX);
      return { ...buffers(data, quadIndices(maxQuads), gl.DYNAMIC_DRAW), data, quads: 0, maxQuads, count: 0, packed: false };
    },
    updateDynamic(mesh) {
      const m = mesh as DynamicMesh & GlMesh;
      m.count = m.quads * 6;
      if (!m.quads) return;
      gl.bindBuffer(gl.ARRAY_BUFFER, m.vbo);
      // Fresh storage each frame, not a write into the old: last frame's draw
      // may still be reading it, and Safari's WebGL (on Metal) waits for it
      // to finish first — a stall that grows with the number of particles.
      gl.bufferData(gl.ARRAY_BUFFER, m.data.subarray(0, m.quads * 4 * FLOATS_PER_VERTEX), gl.DYNAMIC_DRAW);
    },
    free(mesh) {
      const m = mesh as GlMesh;
      gl.deleteBuffer(m.vbo);
      gl.deleteBuffer(m.ibo);
    },
    texture(source) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    },
    resize(width, height) {
      canvas.width = width;
      canvas.height = height;
    },
    render(frame: Frame) {
      const sky = frame.fog;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(sky[0], sky[1], sky[2], 1);
      gl.depthMask(true);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.uniformMatrix4fv(uni.viewProj, false, frame.viewProj);
      gl.uniform3fv(uni.cam, frame.eye);
      gl.uniform3f(uni.fog, sky[0], sky[1], sky[2]);
      gl.uniform1f(uni.time, frame.time);
      gl.uniform1f(uni.look, frame.look);
      const l = frame.light;
      gl.uniform3f(uni.sun, l[0], l[1], l[2]);
      gl.uniform3f(uni.sky, l[4], l[5], l[6]);
      gl.uniform3f(uni.ground, l[8], l[9], l[10]);
      gl.uniform1i(uni.tex, 0);
      gl.bindTexture(gl.TEXTURE_2D, texture);

      for (const pass of PASSES) {
        setBlend(pass);
        for (let i = 0; i < frame.count; i++) {
          const d = frame.draws[i];
          if (d.blend !== pass) continue;
          const m = d.mesh as GlMesh;
          gl.bindBuffer(gl.ARRAY_BUFFER, m.vbo);
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.ibo);
          if (m.packed) {
            gl.vertexAttribPointer(locs.pos, 3, gl.FLOAT, false, PACKED_STRIDE, 0);
            gl.vertexAttribPointer(locs.normal, 3, gl.BYTE, true, PACKED_STRIDE, 12);
            gl.vertexAttribPointer(locs.color, 4, gl.UNSIGNED_BYTE, true, PACKED_STRIDE, 16);
            gl.disableVertexAttribArray(locs.uv);
            gl.vertexAttrib2f(locs.uv, 0, 0);
          } else {
            gl.enableVertexAttribArray(locs.uv);
            gl.vertexAttribPointer(locs.pos, 3, gl.FLOAT, false, FLOATS_PER_VERTEX * F, 0);
            gl.vertexAttribPointer(locs.normal, 3, gl.FLOAT, false, FLOATS_PER_VERTEX * F, 3 * F);
            // Three components: alpha reads as 1, "no paint slot".
            gl.vertexAttribPointer(locs.color, 3, gl.FLOAT, false, FLOATS_PER_VERTEX * F, 6 * F);
            gl.vertexAttribPointer(locs.uv, 2, gl.FLOAT, false, FLOATS_PER_VERTEX * F, 9 * F);
          }
          gl.uniform1f(uni.mode, d.mode);
          gl.uniformMatrix4fv(uni.model, false, d.model);
          gl.uniform4fv(uni.tint, d.tint);
          gl.uniform3fv(uni.body, d.paint?.body ?? WHITE3);
          gl.uniform3fv(uni.accent, d.paint?.accent ?? WHITE3);
          gl.uniform3fv(uni.skin, d.paint?.skin ?? WHITE3);
          gl.uniform3fv(uni.hair, d.paint?.hair ?? WHITE3);
          gl.uniform1f(uni.shine, d.paint ? 1 : 0);
          gl.drawElements(gl.TRIANGLES, m.count, gl.UNSIGNED_SHORT, (m.first ?? 0) * 2);
        }
      }
      gl.depthMask(true);
    },
    // Free what we made, but leave the context alive: the canvas hands the
    // same one back to whoever asks next (StrictMode remounts, for one).
    destroy() {
      gl.deleteTexture(texture);
      gl.deleteProgram(prog);
    },
  };
}
